import { Link } from 'react-router-dom';
import {
  eventFrameOf,
  seatLabel,
  type EventAttendeeCandidate,
  type PromoteAttendeeBody,
} from '@ni/shared';
import { useAttendeeWrites, useEventAttendee } from '../../api/event-hooks';
import { errorLine, isApiError, nameConflictOf } from '../../api/errors';
import { ErrorLine } from '../../components/ui/error-line';
import { Modal } from '../../components/ui/modal';
import { WorkingButton } from '../../components/ui/working-button';
import { IfWrites } from '../../components/write-access';
import {
  ATTENDEE_ADD_CARRIES_PICK,
  ATTENDEE_ADD_LINKED,
  ATTENDEE_ADD_NEW,
  ATTENDEE_ADDING,
  ATTENDEE_BINDING_LABELS,
  ATTENDEE_IDENTIFIER_CONFLICT_LINE,
  ATTENDEE_NAME_CONFLICT_LINE,
  ATTENDEE_NONE_OF_THESE,
  ATTENDEE_NOT_SCANNED,
  ATTENDEE_NO_READINGS,
  ATTENDEE_ON_RECORD_LINE,
  ATTENDEE_OPEN_RECORD,
  ATTENDEE_PICKED,
  ATTENDEE_PICKING,
  ATTENDEE_PROMOTED_LINE,
  ATTENDEE_READINGS_HEAD,
  ATTENDEE_SOMEONE_ELSE,
  ATTENDEE_THIS_IS_THEM,
  ATTENDEE_UNPICK,
  ATTENDEE_USE_THIS_PERSON,
  LOADING,
  eventFrameTierLabel,
} from '../../lib/copy';
import { attendeeDetails } from './attendees';
import { EmptyLine } from '../../components/ui/empty-line';
import { Section } from '../../components/ui/section';

/**
 * **One attendee** (event graph design §3a, §4): what the list gave, the scan's
 * readings of who the name could be with their cited passages, and the
 * office's answers: this reading is them, for this event only (taken back with
 * Not them), or none of these is; and, apart from both, adding them to the
 * records, which brings the picked reading with them. A reading is never
 * identity until the office says so here, and a pick never reaches the record.
 */
export function AttendeeModal({
  eventId,
  nodeId,
  onClose,
}: {
  eventId: string;
  nodeId: string;
  onClose: () => void;
}) {
  const detail = useEventAttendee(eventId, nodeId);
  const { promote, dismiss, pick } = useAttendeeWrites(eventId, nodeId);
  const attendee = detail.data?.attendee ?? null;
  const readings = detail.data?.candidates ?? [];
  const picked = detail.data?.picked_candidate_id ?? null;

  /* The last promote's body, so the conflict answers repeat the same choice of
     reading with the question answered. */
  const last = (promote.variables ?? {
    candidate_id: null,
    entity_id: null,
  }) as PromoteAttendeeBody;
  const conflict = nameConflictOf(promote.error);
  const owners = identifierOwnersOf(promote.error);
  const busy = promote.isPending || dismiss.isPending || pick.isPending;
  const send = (body: Partial<PromoteAttendeeBody>) =>
    promote.mutate({ candidate_id: null, entity_id: null, ...body });

  return (
    <Modal label={attendee?.name ?? LOADING} onClose={onClose}>
      <div className="modal-head">{attendee?.name ?? LOADING}</div>
      {detail.isError ? <ErrorLine error={detail.error} load /> : null}
      {attendee ? (
        <>
          <div className="modal-line src">
            {[attendeeDetails(attendee.identifiers), seatLabel(attendee.identifiers)]
              .filter(Boolean)
              .join(' · ')}
          </div>

          {attendee.promoted_entity_id ? (
            <div className="modal-line">
              {ATTENDEE_PROMOTED_LINE}{' '}
              <Link className="link" to={`/knowledge?entity=${attendee.promoted_entity_id}`}>
                {ATTENDEE_OPEN_RECORD}
              </Link>
            </div>
          ) : attendee.linked ? (
            <div className="modal-line">
              {ATTENDEE_ON_RECORD_LINE}{' '}
              <Link className="link" to={`/knowledge?entity=${attendee.linked.entity_id}`}>
                {attendee.linked.display_name}
              </Link>
            </div>
          ) : null}

          {/* The readings scroll inside the window, so the answers below stay in view. */}
          <Section title={ATTENDEE_READINGS_HEAD}>
            <div className="modal-body">
              {attendee.status === 'pending' && readings.length === 0 ? (
                <EmptyLine>{ATTENDEE_NOT_SCANNED}</EmptyLine>
              ) : readings.length === 0 ? (
                <EmptyLine>{ATTENDEE_NO_READINGS}</EmptyLine>
              ) : (
                readings.map((reading) => (
                  <Reading
                    key={reading.id}
                    reading={reading}
                    picked={reading.id === picked}
                    action={
                      attendee.promoted_entity_id ||
                      attendee.linked ||
                      reading.binding === 'on_record' ? null : reading.id === picked ? (
                        <button
                          type="button"
                          className="linkish flush"
                          disabled={busy}
                          onClick={() => pick.mutate(null)}
                        >
                          {pick.isPending && pick.variables === null
                            ? ATTENDEE_PICKING
                            : ATTENDEE_UNPICK}
                        </button>
                      ) : picked ? null : (
                        /* One pick at a time: Not them comes first. */
                        <WorkingButton
                          label={ATTENDEE_THIS_IS_THEM}
                          workingLabel={ATTENDEE_PICKING}
                          working={pick.isPending && pick.variables === reading.id}
                          disabled={busy}
                          variant="act"
                          tiny
                          onClick={() => pick.mutate(reading.id, { onSuccess: onClose })}
                        />
                      )
                    }
                  />
                ))
              )}
            </div>
          </Section>

          {conflict ? (
            <div className="section">
              <div className="modal-line">{ATTENDEE_NAME_CONFLICT_LINE}</div>
              {conflict.candidates.map((candidate) => (
                <div className="kv-row" key={candidate.entity_id}>
                  <div className="k">{candidate.display_name}</div>
                  <div className="v">
                    <button
                      type="button"
                      className="quiet-action"
                      disabled={busy}
                      onClick={() =>
                        send({ candidate_id: last.candidate_id, entity_id: candidate.entity_id })
                      }
                    >
                      {ATTENDEE_USE_THIS_PERSON}
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="quiet-action"
                disabled={busy}
                onClick={() => send({ candidate_id: last.candidate_id, force_new: true })}
              >
                {ATTENDEE_SOMEONE_ELSE}
              </button>
            </div>
          ) : owners ? (
            <div className="section">
              <div className="modal-line">{ATTENDEE_IDENTIFIER_CONFLICT_LINE}</div>
              {owners.map((owner) => (
                <div className="kv-row" key={owner.entity_id}>
                  <div className="k">{owner.display_name}</div>
                  <div className="v">
                    <button
                      type="button"
                      className="quiet-action"
                      disabled={busy}
                      onClick={() =>
                        send({ candidate_id: last.candidate_id, entity_id: owner.entity_id })
                      }
                    >
                      {ATTENDEE_USE_THIS_PERSON}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : promote.isError ? (
            <div className="modal-line src">{errorLine(promote.error)}</div>
          ) : null}
          {dismiss.isError ? <ErrorLine error={dismiss.error} /> : null}
          {pick.isError ? <ErrorLine error={pick.error} /> : null}
          {picked && !attendee.promoted_entity_id ? (
            <IfWrites>
              <div className="modal-line src">{ATTENDEE_ADD_CARRIES_PICK}</div>
            </IfWrites>
          ) : null}

          {attendee.promoted_entity_id ? null : (
            <IfWrites>
              <div className="modal-actions">
                <WorkingButton
                  label={attendee.linked ? ATTENDEE_ADD_LINKED : ATTENDEE_ADD_NEW}
                  workingLabel={ATTENDEE_ADDING}
                  working={promote.isPending && last.candidate_id === null}
                  disabled={busy}
                  variant="act"
                  onClick={() => send({})}
                />
                {readings.length > 0 ? (
                  <WorkingButton
                    label={ATTENDEE_NONE_OF_THESE}
                    workingLabel={ATTENDEE_NONE_OF_THESE}
                    working={dismiss.isPending}
                    disabled={busy}
                    variant="act"
                    onClick={() => dismiss.mutate(undefined, { onSuccess: onClose })}
                  />
                ) : null}
              </div>
            </IfWrites>
          )}
        </>
      ) : null}
    </Modal>
  );
}

function Reading({
  reading,
  picked,
  action,
}: {
  reading: EventAttendeeCandidate;
  /** The office said this reading is the attendee, for this event. */
  picked: boolean;
  action: React.ReactNode;
}) {
  return (
    <div className="kv-row">
      <div className="k">
        {reading.label}
        <div className="src">{ATTENDEE_BINDING_LABELS[reading.binding]}</div>
        {picked ? <div className="tag-faint block clear-ink">{ATTENDEE_PICKED}</div> : null}
      </div>
      <div className="v">
        {/* How each analysis frame rated this reading, and why. */}
        {reading.assessments.map((assessment) => {
          const frame = eventFrameOf(assessment.frame);
          if (!frame) return null;
          return (
            <div key={assessment.frame}>
              <strong>
                {frame.label}: {eventFrameTierLabel(frame, assessment.tier)}
              </strong>
              <div className="src">{assessment.reason}</div>
            </div>
          );
        })}
        {reading.findings.map((finding) => (
          <div key={finding.id}>
            {citedBy(reading, finding.id) ? (
              <strong>{citedBy(reading, finding.id)} · </strong>
            ) : null}
            {finding.point}
            <div className="src">
              “{finding.quote}”
              {finding.url ? (
                <>
                  {' '}
                  <a className="link" href={finding.url} target="_blank" rel="noreferrer">
                    source
                  </a>
                </>
              ) : null}
            </div>
          </div>
        ))}
        {action ? <IfWrites>{action}</IfWrites> : null}
      </div>
    </div>
  );
}

/** The frames whose rating other than clear rests on this finding, as their tier labels. */
function citedBy(reading: EventAttendeeCandidate, findingId: string): string | null {
  const labels = reading.assessments
    .filter((assessment) => assessment.finding_ids.includes(findingId))
    .flatMap((assessment) => {
      const frame = eventFrameOf(assessment.frame);
      return frame ? [eventFrameTierLabel(frame, assessment.tier)] : [];
    });
  return labels.length > 0 ? labels.join(', ') : null;
}

/** `IDENTIFIER_ON_RECORD` carries the people who already hold the email or phone. */
function identifierOwnersOf(error: unknown): { entity_id: string; display_name: string }[] | null {
  if (!isApiError(error) || error.code !== 'IDENTIFIER_ON_RECORD') return null;
  const candidates = (error.details as { candidates?: unknown } | undefined)?.candidates;
  return Array.isArray(candidates)
    ? (candidates as { entity_id: string; display_name: string }[])
    : null;
}
